type Row = Record<string, unknown>;

/** The cast already consumed these catalog layers before its item became a status.
 * They neither describe a lingering effect nor leave a compiled contribution to undo.
 * Keep explicit names: e.g. spell_concentration, target_marked_by_caster and each-turn
 * charges are live effects, not members of a spell_ / target_ / charge prefix rule. */
export const CASTING_ONLY_STATUS_LAYERS: ReadonlySet<string> = new Set([
  'spell_component_verbal', 'spell_component_somatic', 'spell_component_material',
  'spell_component_material_consumed', 'spell_material_item_id',
  'spell_level', 'spell_casting_time', 'spell_circle', 'spell_sphere', 'spell_ritual',
  'spell_upcastable', 'spell_scalable_cantrip', 'dice_upcast', 'die_upcast',
  'spell_upcast_additional_projectiles_created', 'spell_upcast_additional_targets_int',
  'spell_upcast_duration_int', 'spell_upcast_duration_type', 'spell_upcast_increase_aoe_ft',
  'cost_gp', 'cost_gp_multiplier', 'ability_activation_charges', 'ability_charges',
  'consumes_ability_charges', 'consumes_charges_qty', 'recharge_range_begin', 'recharge_range_end',
  'range', 'max_targets', 'chooseable_targets_in_aoe',
  'requires_class', 'requires_class_level', 'requires_subclass', 'requires_level',
  'status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_target_after_cast', 'stacks_applied',
]);

export const isCastingOnlyStatusLayer = (name: string): boolean => CASTING_ONLY_STATUS_LAYERS.has(name);

/** These effects read the saved status at use/turn time; they retain no derived cluster.
 * Both status projectors use this classification. Casting-only fields remain
 * separate because expiry must still inspect the live effect's own payload. */
export const STATUS_RUNTIME_ONLY_LAYERS: ReadonlySet<string> = new Set([
  'unenchantable', 'duration_int', 'duration_type', 'ability_resets_long_rest', 'assistable_status', 'status_effect_pausable',
  'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn',
  'effect_ends_on_target_start_of_turn', 'effect_ends_on_target_end_of_turn',
  'roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis',
  // Incoming effects and bag access read the held mark's applier; it has no derived cluster.
  'target_marked_by_caster',
  // Casts and recurring contests select these skills at use time.
  'skill_roll_contester', 'skill_roll_contestee',
  // Forced dice and next-use listeners read the current saved status, not a compiled cluster.
  'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min',
  'triggers_on_next_roll_type', 'triggers_when_caster_uses_item_type',
  'no_actions', 'no_reactions', 'ap_change_at_start_of_turn',
  'dice', 'die', 'mod', 'roll_type', 'damage_type', 'buff_does_not_layer_onto_opposing_team', 'triggers_on_self_zero_vitality',
  // Damage listeners and their outcome predicates read the live row at dispatch time.
  'triggers_when_caster_takes_damage_type', 'triggers_when_target_takes_damage_type',
  'triggers_when_caster_makes_roll_type',
  'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit', 'status_effect_applied_to_target_on_hit',
]);
export const statusRowOnlyLayer = (name: string): boolean => isCastingOnlyStatusLayer(name)
  || STATUS_RUNTIME_ONLY_LAYERS.has(name) || /^requires_skill_tier_\d+$/.test(name);

/** Shared by expiry admission and the projection used by turns, attacks and GM cascades. */
export function statusRuntimeLayerKeys(definition: Row | null): string[] {
  const layers = definition?.item_layer_types;
  return layers && typeof layers === 'object' && !Array.isArray(layers)
    ? Object.keys(layers).filter(key => !isCastingOnlyStatusLayer(key)) : [];
}
